import { useState } from 'react';
import { ResourcesTable } from './table';
import { useFullResource } from './table/columns/actions/use-full-resource';
import { ResourceListItem } from './types';
import {
  ResourcePageListFilterInput,
  ResourcePageType,
  SortingDirection,
} from 'src/graphql/generated/graphql';
import { useTable } from 'src/hooks/use-table';
import { MUISortOptions } from 'mui-datatables';
import { useResources } from 'src/hooks/query/resources/use-resources';
import { useNavigate } from 'react-router-dom';
import { VideoModal } from './table/columns/actions/video-modal';
import { Routes } from 'src/components/common/router/constants';
import { useAuthContext } from 'src/context/auth.context';

const PAGINATION_LIMIT = 10;

interface ResourceListTableProps {
  isUserResources?: boolean;
  isBookmarksView?: boolean;
  filter?: ResourcePageListFilterInput;
  defaultSorting?: Record<string, SortingDirection>;
  limit?: number;
  withoutPagination?: boolean;
  excludeColumns?: string[];
}

export const ResourceListTable = ({
  isUserResources = false,
  isBookmarksView = false,
  filter,
  defaultSorting = {
    name: SortingDirection.Asc,
  },
  limit = PAGINATION_LIMIT,
  withoutPagination = false,
  excludeColumns = [],
}: ResourceListTableProps) => {
  const { user } = useAuthContext();

  const [selectedResource, setSelectedResource] =
    useState<ResourceListItem | null>(null);

  const { onDownload, onPlay, onClosePlay, fullResource, isPlaying } =
    useFullResource({ resource: selectedResource });

  const navigate = useNavigate();

  const {
    page,
    setPage,
    sorting,
    onSortChange,
    sortingField,
    sortingDirection,
  } = useTable<ResourcePageListFilterInput, Record<string, SortingDirection>>({
    defaultSorting,
  });

  const sortOrder: MUISortOptions | undefined = sortingField
    ? {
        name: sortingField,
        direction: sortingDirection === SortingDirection.Desc ? 'desc' : 'asc',
      }
    : undefined;

  // Resources
  const {
    loading: resourcesLoading,
    resources,
    totalCount: resourcesTotalCount,
  } = useResources({
    filter: {
      ...filter,
      isDisabled:
        typeof filter?.isDisabled === 'boolean'
          ? filter?.isDisabled
          : undefined,
    },
    sorting,
    pagination: {
      page,
      limit,
    },
    isUserResources,
    skip: isBookmarksView,
  });

  // Bookmarked resources
  const {
    loading: bookmarksLoading,
    resources: bookmarkedResources,
    totalCount: bookmarksTotalCount,
  } = useResources({
    filter: {
      ...filter,
      isDisabled:
        typeof filter?.isDisabled === 'boolean'
          ? filter?.isDisabled
          : undefined,
      bookmarkUserId: user?.id,
      bookmarkOrganizationId: user?.organizationId,
    },
    sorting,
    pagination: {
      page,
      limit,
    },
    isUserResources,
    skip: !isBookmarksView,
  });

  const totalCount = !isBookmarksView
    ? resourcesTotalCount
    : bookmarksTotalCount;

  const onRowClick = (_: any, { dataIndex }: { dataIndex: number }) => {
    const resource = !isBookmarksView
      ? resources[dataIndex]
      : bookmarkedResources[dataIndex];

    if (!isUserResources) {
      navigate(
        `${Routes.adminResources.path}/${resource.id}/edit?type=${resource.type}`,
      );
    } else {
      setSelectedResource(resource);
      if (resource.type === ResourcePageType.Video) {
        onPlay();
      } else {
        onDownload();
      }
    }
  };

  return (
    <>
      <ResourcesTable
        data={!isBookmarksView ? resources : bookmarkedResources}
        className="mt-4"
        loading={resourcesLoading || bookmarksLoading}
        options={{
          page,
          count: withoutPagination ? limit : totalCount,
          sort: true,
          sortOrder,
          rowsPerPage: limit,
          onRowClick,
          onChangePage: setPage,
          onColumnSortChange: onSortChange,
          enableNestedDataAccess: '.',
        }}
        excludeColumns={excludeColumns}
      />

      {selectedResource?.type === ResourcePageType.Video && isPlaying && (
        <VideoModal
          resource={fullResource?.resourceById}
          onClose={onClosePlay}
        />
      )}
    </>
  );
};
